import html2canvas from 'html2canvas'
import { ScreenshotOptions } from './types'

export class ScreenshotCapture {
  private defaultOptions: ScreenshotOptions = {
    quality: 0.8,
    format: 'png',
    backgroundColor: '#ffffff'
  }

  async capture(options: ScreenshotOptions = {}): Promise<Blob> {
    const finalOptions = { ...this.defaultOptions, ...options }
    
    try {
      const canvas = await html2canvas(document.body, {
        backgroundColor: finalOptions.backgroundColor,
        useCORS: true,
        allowTaint: true,
        scale: 0.5, // Reduce size for better performance
        width: Math.min(window.innerWidth, 1920),
        height: Math.min(window.innerHeight, 1080),
        scrollX: 0,
        scrollY: 0,
        ignoreElements: (element) => {
          // Ignore ExitBug elements to avoid capturing the widget itself
          return element.id?.startsWith('feedlet-') || 
                 element.className?.includes('feedlet-') ||
                 element.getAttribute('data-feedlet') === 'true'
        }
      })

      return new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob)
            } else {
              reject(new Error('Failed to create screenshot blob'))
            }
          },
          `image/${finalOptions.format}`,
          finalOptions.quality
        )
      })
    } catch (error) {
      throw new Error(`Screenshot capture failed: ${error}`)
    }
  }

  async captureElement(element: HTMLElement, options: ScreenshotOptions = {}): Promise<Blob> {
    const finalOptions = { ...this.defaultOptions, ...options }
    
    try {
      const canvas = await html2canvas(element, {
        backgroundColor: finalOptions.backgroundColor,
        useCORS: true,
        allowTaint: true,
        scale: 1
      })

      return new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob)
            } else {
              reject(new Error('Failed to create element screenshot blob'))
            }
          },
          `image/${finalOptions.format}`,
          finalOptions.quality
        )
      })
    } catch (error) {
      throw new Error(`Element screenshot capture failed: ${error}`)
    }
  }

  isSupported(): boolean {
    return typeof window !== 'undefined' && 
           typeof document !== 'undefined' && 
           typeof HTMLCanvasElement !== 'undefined'
  }

  async getDataUrl(options: ScreenshotOptions = {}): Promise<string> {
    const blob = await this.capture(options)
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }
} 